import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Send
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExamPage = () => {
  const { courseId, enrollmentId } = useParams();
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const [examData, setExamData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(false);  // Changed to false initially
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [attemptId, setAttemptId] = useState(null);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Submit exam handler
  const handleSubmit = useCallback(async (isAutoSubmit = false) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const answersList = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        question_id: questionId,
        selected_answer: selectedAnswer
      }));

      const response = await axios.post(`${API}/assessment/submit-exam`, {
        attempt_id: attemptId,
        answers: answersList
      });

      toast.success(isAutoSubmit ? 'Time up! Exam submitted.' : 'Exam submitted successfully!');
      
      // Navigate to results page
      navigate(`/assessment/results/${attemptId}`, { 
        state: { result: response.data } 
      });
    } catch (error) {
      toast.error('Failed to submit exam');
      setSubmitting(false);
    }
  }, [answers, attemptId, navigate, submitting]);

  // Timer effect
  useEffect(() => {
    if (!examStarted || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, timeLeft, handleSubmit]);

  // Start exam
  const startExam = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to take the exam');
      navigate('/auth/login');
      return;
    }

    setLoading(true);
    try {
      // Get the paper for this course first
      const papersRes = await axios.get(`${API}/assessment/papers/course/${courseId}`);
      if (!papersRes.data.length) {
        toast.error('No exam paper available for this course');
        navigate('/assessment');
        return;
      }

      const paper = papersRes.data[0];
      
      // Start the exam
      const response = await axios.post(`${API}/assessment/start-exam?paper_id=${paper.id}&enrollment_id=${enrollmentId}`);
      
      setExamData(response.data);
      setQuestions(response.data.questions);
      setAttemptId(response.data.attempt_id);
      setTimeLeft(response.data.duration_minutes * 60);
      setExamStarted(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start exam';
      toast.error(message);
      if (message.includes('Payment required') || message.includes('already attempted')) {
        navigate('/assessment');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
    }
  };

  const getAnsweredCount = () => Object.keys(answers).length;
  const getUnansweredCount = () => questions.length - getAnsweredCount();

  const currentQuestion = questions[currentQuestionIndex];

  if (loading && !examStarted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!examStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="h-10 w-10 text-blue-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Ready to Start Your Exam?</h1>
          <p className="text-slate-600 mb-6">Please read the instructions carefully before starting.</p>
          
          <div className="bg-slate-50 rounded-lg p-6 text-left mb-6">
            <h3 className="font-semibold text-slate-900 mb-3">Exam Instructions:</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>You have 45 minutes to complete the exam</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>There are 20 multiple choice questions</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Each question carries 5 marks (Total: 100 marks)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>50% marks (50 marks) required to pass</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>No negative marking for wrong answers</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span>Exam will auto-submit when time runs out</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span>Do not refresh or close the browser during exam</span>
              </li>
            </ul>
          </div>
          
          <Button 
            className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
            onClick={startExam}
            data-testid="start-exam-btn"
          >
            Start Exam
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header with Timer */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-bold text-slate-900">{examData?.paper?.paper_name}</h1>
          </div>
          
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-lg font-bold ${
            timeLeft < 300 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-blue-100 text-blue-700'
          }`}>
            <Clock className="h-5 w-5" />
            <span data-testid="timer">{formatTime(timeLeft)}</span>
          </div>
          
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setShowConfirmDialog(true)}
            disabled={submitting}
            data-testid="submit-exam-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Submit Exam
          </Button>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Question Navigation Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 p-4 hidden md:block">
          <div className="mb-4">
            <p className="text-sm text-slate-600">Progress</p>
            <p className="font-bold text-slate-900">{getAnsweredCount()} / {questions.length} answered</p>
          </div>
          
          <div className="grid grid-cols-5 gap-2">
            {questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => goToQuestion(index)}
                className={`w-10 h-10 rounded-lg font-medium text-sm transition-colors ${
                  index === currentQuestionIndex
                    ? 'bg-blue-600 text-white'
                    : answers[q.id]
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`nav-q-${index + 1}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-100 border border-emerald-300 rounded"></div>
              <span className="text-slate-600">Answered ({getAnsweredCount()})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-100 rounded"></div>
              <span className="text-slate-600">Not Answered ({getUnansweredCount()})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-600 rounded"></div>
              <span className="text-slate-600">Current</span>
            </div>
          </div>
        </aside>

        {/* Main Question Area */}
        <main className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">
            {currentQuestion && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="question-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <span className="text-sm text-slate-500">5 marks</span>
                </div>
                
                <h2 className="text-xl font-medium text-slate-900 mb-6" data-testid="question-text">
                  {currentQuestion.question_text}
                </h2>
                
                <div className="space-y-3">
                  {['A', 'B', 'C', 'D'].map((option) => {
                    const optionKey = `option_${option.toLowerCase()}`;
                    const isSelected = answers[currentQuestion.id] === option;
                    
                    return (
                      <button
                        key={option}
                        onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                        data-testid={`option-${option}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                            isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {option}
                          </span>
                          <span className="text-slate-900">{currentQuestion[optionKey]}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={() => goToQuestion(currentQuestionIndex - 1)}
                    disabled={currentQuestionIndex === 0}
                    data-testid="prev-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  
                  <span className="text-sm text-slate-500 hidden sm:block">
                    Use keyboard: ← → to navigate, A/B/C/D to answer
                  </span>
                  
                  <Button
                    variant="outline"
                    onClick={() => goToQuestion(currentQuestionIndex + 1)}
                    disabled={currentQuestionIndex === questions.length - 1}
                    data-testid="next-btn"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-600">{getAnsweredCount()} / {questions.length} answered</span>
          <span className={`font-mono font-bold ${timeLeft < 300 ? 'text-red-600' : 'text-blue-600'}`}>
            {formatTime(timeLeft)}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => goToQuestion(currentQuestionIndex - 1)}
            disabled={currentQuestionIndex === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            className="flex-1 bg-emerald-600"
            onClick={() => setShowConfirmDialog(true)}
          >
            Submit
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => goToQuestion(currentQuestionIndex + 1)}
            disabled={currentQuestionIndex === questions.length - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Confirm Submit Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Exam?</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{getAnsweredCount()}</p>
                  <p className="text-sm text-slate-600">Answered</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">{getUnansweredCount()}</p>
                  <p className="text-sm text-slate-600">Unanswered</p>
                </div>
              </div>
            </div>
            
            {getUnansweredCount() > 0 && (
              <div className="flex items-start gap-2 p-3 bg-orange-50 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-orange-700">
                  You have {getUnansweredCount()} unanswered question(s). 
                  Unanswered questions will be marked as incorrect.
                </p>
              </div>
            )}
            
            <p className="text-slate-600">
              Are you sure you want to submit your exam? This action cannot be undone.
            </p>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowConfirmDialog(false)}
              >
                Continue Exam
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleSubmit(false)}
                disabled={submitting}
                data-testid="confirm-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Exam'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExamPage;
